(() => {
  const desktop = window.matchMedia('(min-width: 900px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new WeakMap();
  const mobileHeroTransitions = new WeakMap();
  const mobileShowcaseTransitions = new WeakMap();

  const findRoots = (scope) => {
    if (!scope) return [];
    const roots = [];
    if (scope.matches?.('[data-kvsf-section]')) roots.push(scope);
    roots.push(...scope.querySelectorAll?.('[data-kvsf-section]') || []);
    return roots;
  };

  const swapImage = (image, desktopSource, mobileSource, alt) => {
    if (!image) return;
    const source = desktop.matches ? desktopSource : mobileSource || desktopSource;
    if (!source || image.dataset.kvsfSource === source) return;
    const media = image.closest('.kvsf-hero__media');
    const showcaseFrame = image.closest('.kvsf-showcase__room, .kvsf-showcase__texture');
    const useMobileHeroTransition = !desktop.matches && media && !reducedMotion.matches;
    const useMobileShowcaseTransition = !desktop.matches && showcaseFrame && !reducedMotion.matches;
    if (!useMobileHeroTransition && !useMobileShowcaseTransition) image.classList.add('kvsf-is-switching');
    const preload = new Image();
    const apply = () => {
      const outgoingSource = image.currentSrc || image.src;

      if (useMobileHeroTransition && outgoingSource) {
        const previous = mobileHeroTransitions.get(image);
        if (previous) {
          clearTimeout(previous.timer);
          previous.outgoing.remove();
        }

        const outgoing = image.cloneNode(false);
        outgoing.removeAttribute('data-kvsf-hero-image');
        outgoing.classList.remove('kvsf-is-switching', 'kvsf-mobile-finish-in');
        outgoing.classList.add('kvsf-mobile-finish-out');
        outgoing.src = outgoingSource;
        outgoing.srcset = '';
        outgoing.alt = '';
        outgoing.setAttribute('aria-hidden', 'true');
        media.appendChild(outgoing);

        image.classList.add('kvsf-mobile-finish-in');
        const timer = window.setTimeout(() => {
          outgoing.remove();
          image.classList.remove('kvsf-mobile-finish-in');
          mobileHeroTransitions.delete(image);
        }, 820);
        mobileHeroTransitions.set(image, { outgoing, timer });
      }

      if (useMobileShowcaseTransition && outgoingSource) {
        const previous = mobileShowcaseTransitions.get(image);
        if (previous) {
          clearTimeout(previous.timer);
          previous.outgoing.remove();
        }

        const outgoing = image.cloneNode(false);
        outgoing.removeAttribute('data-kvsf-room');
        outgoing.removeAttribute('data-kvsf-texture');
        outgoing.classList.remove('kvsf-is-switching', 'kvsf-showcase-image-in');
        outgoing.classList.add('kvsf-showcase-image-out');
        outgoing.src = outgoingSource;
        outgoing.srcset = '';
        outgoing.alt = '';
        outgoing.setAttribute('aria-hidden', 'true');
        showcaseFrame.appendChild(outgoing);

        image.classList.add('kvsf-showcase-image-in');
        const timer = window.setTimeout(() => {
          outgoing.remove();
          image.classList.remove('kvsf-showcase-image-in');
          mobileShowcaseTransitions.delete(image);
        }, 620);
        mobileShowcaseTransitions.set(image, { outgoing, timer });
      }

      image.src = source;
      image.srcset = '';
      image.dataset.kvsfSource = source;
      image.alt = alt || '';
      if (!useMobileHeroTransition && !useMobileShowcaseTransition) requestAnimationFrame(() => image.classList.remove('kvsf-is-switching'));
    };
    preload.addEventListener('load', apply, { once: true });
    preload.addEventListener('error', apply, { once: true });
    preload.src = source;
  };

  const setHeroFinish = (root, handle) => {
    const controls = [...root.querySelectorAll('[data-kvsf-finish]')];
    const control = controls.find((item) => item.dataset.kvsfFinish === handle);
    if (!control) return;
    controls.forEach((item) => item.setAttribute('aria-pressed', String(item === control)));
    root.style.setProperty('--accent', control.querySelector('.kvsf__finish-dot')?.style.getPropertyValue('--finish-color') || '#c7c7c2');
    swapImage(root.querySelector('[data-kvsf-hero-image]'), control.dataset.room, control.dataset.roomMobile, control.dataset.roomAlt);
  };

  const stepHeroFinish = (root, direction) => {
    const controls = [...root.querySelectorAll('[data-kvsf-finish]')];
    if (!controls.length) return;
    const current = controls.findIndex((item) => item.getAttribute('aria-pressed') === 'true');
    const start = current >= 0 ? current : 0;
    const next = (start + direction + controls.length) % controls.length;
    setHeroFinish(root, controls[next].dataset.kvsfFinish);
  };

  const setShowcaseFinish = (root, handle) => {
    const tabs = [...root.querySelectorAll('[data-kvsf-tab]')];
    const selectedTab = tabs.find((tab) => tab.dataset.kvsfFinish === handle);
    if (!selectedTab) return;
    const panels = [...root.querySelectorAll('[data-kvsf-panel]')];
    tabs.forEach((tab) => {
      const active = tab === selectedTab;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    panels.forEach((panel) => {
      panel.hidden = panel.id !== selectedTab.getAttribute('aria-controls');
    });
    const panel = root.querySelector(`#${CSS.escape(selectedTab.getAttribute('aria-controls'))}`);
    if (!panel) return;
    root.style.setProperty('--accent', panel.dataset.color || '#c7c7c2');
    swapImage(root.querySelector('[data-kvsf-room]'), panel.dataset.room, panel.dataset.roomMobile, panel.dataset.roomAlt);
    swapImage(root.querySelector('[data-kvsf-texture]'), panel.dataset.texture, panel.dataset.textureMobile, panel.dataset.textureAlt);
  };

  const initReveal = (root, cleanup) => {
    if (reducedMotion.matches || !('IntersectionObserver' in window)) return;
    root.classList.add('kvsf-motion-ready');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('kvsf-is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12 });
    root.querySelectorAll('.kvsf__reveal').forEach((element) => observer.observe(element));
    cleanup.push(() => observer.disconnect());
  };

  const initHero = (root, cleanup) => {
    root.querySelectorAll('[data-kvsf-finish]').forEach((button) => {
      const onClick = () => {
        if (!desktop.matches) return;
        setHeroFinish(root, button.dataset.kvsfFinish);
      };
      button.addEventListener('click', onClick);
      cleanup.push(() => button.removeEventListener('click', onClick));
    });
    root.querySelectorAll('[data-kvsf-scroll-target]').forEach((button) => {
      const onClick = () => {
        const container = root.closest('main') || document;
        const target = container.querySelector(`[data-kvsf-anchor="${CSS.escape(button.dataset.kvsfScrollTarget)}"]`);
        target?.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      };
      button.addEventListener('click', onClick);
      cleanup.push(() => button.removeEventListener('click', onClick));
    });

    const card = root.querySelector('.kvsf-hero__card');
    if (card) {
      let touchStartX = 0;
      let touchStartY = 0;
      let touchId = null;
      const onTouchStart = (event) => {
        if (desktop.matches || !event.changedTouches.length) return;
        const touch = event.changedTouches[0];
        touchId = touch.identifier;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      };
      const onTouchEnd = (event) => {
        if (desktop.matches || touchId === null) return;
        const touch = [...event.changedTouches].find((item) => item.identifier === touchId);
        if (!touch) return;
        const deltaX = touch.clientX - touchStartX;
        const deltaY = touch.clientY - touchStartY;
        touchId = null;
        if (Math.abs(deltaX) < 44 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;
        stepHeroFinish(root, deltaX < 0 ? 1 : -1);
      };
      const onTouchCancel = () => {
        touchId = null;
      };
      card.addEventListener('touchstart', onTouchStart, { passive: true });
      card.addEventListener('touchend', onTouchEnd, { passive: true });
      card.addEventListener('touchcancel', onTouchCancel, { passive: true });
      cleanup.push(() => {
        card.removeEventListener('touchstart', onTouchStart);
        card.removeEventListener('touchend', onTouchEnd);
        card.removeEventListener('touchcancel', onTouchCancel);
      });
    }

    const scrollRoot = root.querySelector('[data-kvsf-hero-scroll]');
    if (!scrollRoot) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      if (!desktop.matches || reducedMotion.matches || !root.isConnected) return;
      const available = Math.max(scrollRoot.offsetHeight - window.innerHeight, 1);
      const progress = Math.max(0, Math.min(1, -scrollRoot.getBoundingClientRect().top / available));
      const reveal = Math.max(0, Math.min(1, (progress - 0.08) / 0.42));
      const detail = Math.max(0, Math.min(1, (progress - 0.42) / 0.28));
      root.style.setProperty('--image-height', `${20 + reveal * 80}%`);
      root.style.setProperty('--hero-shade', `${0.12 + progress * 0.58}`);
      root.style.setProperty('--image-scale', `${1.06 - progress * 0.06}`);
      root.style.setProperty('--title-y', `${-detail * 52}px`);
      root.style.setProperty('--copy-opacity', detail.toFixed(2));
      root.style.setProperty('--copy-y', `${(1 - detail) * 12}px`);
      root.style.setProperty('--selector-opacity', detail.toFixed(2));
      root.style.setProperty('--selector-y', `${(1 - detail) * 12}px`);
    };
    const queue = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    desktop.addEventListener('change', queue);
    reducedMotion.addEventListener('change', queue);
    update();
    cleanup.push(() => {
      window.removeEventListener('scroll', queue);
      window.removeEventListener('resize', queue);
      desktop.removeEventListener('change', queue);
      reducedMotion.removeEventListener('change', queue);
      if (frame) cancelAnimationFrame(frame);
    });
  };

  const initShowcase = (root, cleanup) => {
    const tabs = [...root.querySelectorAll('[data-kvsf-tab]')];
    tabs.forEach((tab) => {
      const onClick = () => setShowcaseFinish(root, tab.dataset.kvsfFinish);
      tab.addEventListener('click', onClick);
      cleanup.push(() => tab.removeEventListener('click', onClick));
    });
    const tablist = root.querySelector('[data-kvsf-tabs]');
    if (!tablist || !tabs.length) return;
    const onKeydown = (event) => {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      const current = event.target.closest('[data-kvsf-tab]');
      if (!current) return;
      event.preventDefault();
      const index = tabs.indexOf(current);
      const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      const next = tabs[nextIndex];
      next.focus();
      setShowcaseFinish(root, next.dataset.kvsfFinish);
    };
    tablist.addEventListener('keydown', onKeydown);
    cleanup.push(() => tablist.removeEventListener('keydown', onKeydown));
  };

  const init = (root) => {
    if (!root || instances.has(root)) return;
    const cleanup = [];
    instances.set(root, () => {
      cleanup.splice(0).forEach((dispose) => dispose());
      instances.delete(root);
      root.classList.remove('kvsf-motion-ready');
    });
    initReveal(root, cleanup);
    if (root.hasAttribute('data-kvsf-hero')) initHero(root, cleanup);
    if (root.hasAttribute('data-kvsf-showcase')) initShowcase(root, cleanup);
  };

  const destroy = (root) => instances.get(root)?.();
  const boot = (scope = document) => findRoots(scope).forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => findRoots(event.target).forEach(destroy));
})();
